import React, { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { WorkoutCellInnerProps } from './types';
import { WorkoutCellViewModel } from './WorkoutCellViewModel';
import { H5, P2 } from 'styleguide/Texts';
import { ProfileImage } from 'components/ProfileImage';
import { View } from 'styleguide/View';
import { Card } from 'styleguide/Card';
import { Stats } from './components/Stats';
import { WorkoutActions } from '../WorkoutActions';
import { DividerLine } from 'styleguide/DividerLine';
import { WorkoutContent } from './components/WorkoutContent';
import { Comments } from './components/Comments';
import { CommentInput } from './components/CommentInput';
import { Link } from 'components/Link';
import { modal } from 'components/Modals/ModalManager';
import { Spacing } from 'styleguide/spacing';
import { LinkableText } from 'components/LinkableText';
import { FlexRow } from 'styleguide/Row';
import { OnlyYouIndicator } from 'components/OnlyYouIndicator';

const Container = styled(Card)`
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacing.md}px;
`;

const TopContainer = styled(View)`
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
  padding-bottom: ${Spacing.md}px;
`;

const MiddleContainer = styled(View)`
  gap: ${Spacing.md}px;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  height: 100%;
`;

const BottomContainer = styled(View)`
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
  padding-top: ${Spacing.md}px;
`;

export interface WorkoutCellProps {
  vm: WorkoutCellViewModel;
  unauthenticatedView?: boolean;
}

export const WorkoutCell = observer(({ vm, unauthenticatedView }: WorkoutCellProps) => {
  return <WorkoutCellInner {...vm.data} unauthenticatedView={unauthenticatedView} />;
});

const WorkoutCellInner = (props: WorkoutCellInnerProps) => {
  const commentInput = useRef<HTMLTextAreaElement | null>(null);

  return (
    <Container>
      <TopContainer>
        <Link href={`/user/${props.username}`}>
          <FlexRow>
            <ProfileImage source={props.profileImage} diameter={48} />
            <View style={{ marginInlineStart: Spacing.md }}>
              <H5>{props.username}</H5>
              <FlexRow style={{ gap: Spacing.xs }}>
                <P2 type="secondary">{props.date}</P2>
                {props.privateWorkout && <OnlyYouIndicator isCoached={props.isCoached} />}
              </FlexRow>
            </View>
          </FlexRow>
        </Link>

        <Link href={`/workout/${props.shortId || props.workoutId}`}>
          <View style={{ marginBottom: Spacing.md }}>
            <H5>{props.title}</H5>
            {props.description && <LinkableText enableMentions text={props.description} />}
          </View>
          <Stats stats={props.stats} />
        </Link>
      </TopContainer>

      <View style={{ padding: `0px ${Spacing.lg}px` }}>
        <DividerLine />
      </View>

      <MiddleContainer>
        <Link href={`/workout/${props.shortId || props.workoutId}`}>
          <WorkoutContent {...props.content} unauthenticatedView={props.unauthenticatedView} />
        </Link>
      </MiddleContainer>

      {!props.unauthenticatedView && (
        <View>
          <WorkoutActions
            isLiked={props.isLiked}
            onShowLikes={() =>
              modal.showUserListModal({ mode: 'likes', workoutId: props.workoutId })
            }
            likeCount={props.likeCount}
            commentCount={props.commentCount}
            onLike={props.onLike}
            onComment={() => modal.openCommentModal(props.workoutId)}
            onShare={props.onShare}
          />
          <BottomContainer>
            {!!props.comments.length && (
              <Comments
                comments={props.comments}
                workout={props.workout}
                onCommentDelete={props.onCommentDelete}
              />
            )}
            <CommentInput
              profilePic={props.currentUserImage}
              onSend={props.onCommentSend}
              inputRef={commentInput}
            />
          </BottomContainer>
        </View>
      )}
    </Container>
  );
};
